import { firebaseConfig, useEmulators } from '../config/firebase';

const FUNCTIONS_REGION = 'us-central1';
const FUNCTION_NAME = 'sendConversionEvent';

const getSendConversionEventUrl = () => {
  if (useEmulators) {
    return `http://localhost:5001/${firebaseConfig.projectId}/${FUNCTIONS_REGION}/${FUNCTION_NAME}`;
  }
  return `https://${FUNCTIONS_REGION}-${firebaseConfig.projectId}.cloudfunctions.net/${FUNCTION_NAME}`;
};

/**
 * Fire server-side conversion events for a completed screen or checkpoint.
 * Uses fetch + keepalive so the request can outlive a fast page exit
 * (unlike Firebase httpsCallable, which the browser may cancel on unload).
 * Non-blocking — callers should not await unless they need the result.
 */
export const sendConversionEvent = async ({
  shortcode,
  itemKey,
  itemType,
  conversionValueOverride = null
}) => {
  const payload = {
    data: {
      shortcode,
      itemKey,
      itemType,
      eventSourceUrl: window.location.href,
      clientUserAgent: navigator.userAgent,
      conversionValueOverride
    }
  };

  const response = await fetch(getSendConversionEventUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    // Survives tab close / navigation after the request is queued (~64KB body limit).
    keepalive: true
  });

  let body;
  try {
    body = await response.json();
  } catch {
    throw new Error(
      `sendConversionEvent: non-JSON response (HTTP ${response.status})`
    );
  }

  // Callable error protocol: { error: { status, message, details } }
  if (body?.error) {
    const err = new Error(body.error.message || 'sendConversionEvent failed');
    err.code = body.error.status;
    err.details = body.error.details;
    throw err;
  }

  if (!response.ok) {
    throw new Error(`sendConversionEvent failed (HTTP ${response.status})`);
  }

  // Match httpsCallable shape: { data: <function return value> }
  return { data: body.result };
};

const PLATFORM_LABELS = {
  meta: 'Meta',
  linkedin: 'LinkedIn',
  google: 'Google Ads'
};

const platformTargetLabel = (platform, targets = {}) => {
  if (platform === 'meta') return targets.metaEventName || '(no event name)';
  if (platform === 'linkedin') {
    const raw = String(targets.linkedinConversionRuleUrn || '').trim();
    if (!raw) return '(no conversion URN)';
    const match = raw.match(/(\d+)\s*$/);
    return match ? match[1] : raw;
  }
  if (platform === 'google') return targets.googleConversionActionId || '(no conversion action ID)';
  return '';
};

/**
 * Log the per-platform status returned by the sendConversionEvent callable so
 * testers can track each conversion call from the browser console.
 */
const logConversionResults = (params, data) => {
  const { itemKey, itemType, platformTargets } = params;
  const label = `[Conversion] ${itemType || 'item'} "${itemKey}"`;

  if (!data || data.skipped) {
    console.log(`${label} — no events sent (${data?.reason || 'skipped'})`);
    return;
  }

  console.log(`${label} — event dispatched (eventId: ${data.eventId || 'n/a'})`);

  ['meta', 'linkedin', 'google'].forEach((platform) => {
    const result = data[platform];
    const name = PLATFORM_LABELS[platform];
    if (!result) {
      return;
    }

    const target = platformTargetLabel(platform, platformTargets);

    if (result.validated) {
      console.log(
        `  ✔ ${name}: VALIDATED ${target} (test payload accepted, no conversion recorded)`,
        result.response ?? ''
      );
    } else if (result.sent) {
      console.log(`  ✔ ${name}: SENT ${target}`, result.response ?? '');
    } else if (result.error) {
      console.error(`  ✖ ${name}: ERROR ${target} — ${result.error}`);
    } else if (result.skipped) {
      console.log(`  – ${name}: SKIPPED ${target} (${result.reason || 'unknown'})`);
    } else {
      console.log(`  ? ${name}: no status ${target}`, result);
    }
  });
};

// Prevent duplicate platform uploads when complete/load fires more than once
// (double-click, overlapping handlers, etc.) within the same browser session.
const firedConversionKeys = new Set();

/**
 * Fire conversion events without blocking navigation.
 */
export const sendConversionEventAsync = (params) => {
  sendConversionEvent(params)
    .then((response) => {
      logConversionResults(params, response?.data);
    })
    .catch((error) => {
      console.error(
        `[Conversion] ${params.itemType || 'item'} "${params.itemKey}" — call failed:`,
        error
      );
    });
};

/**
 * Resolve conversion value override from checkpoint payment amount when configured.
 */
export const getConversionValueOverride = (itemData, response = null) => {
  const config = itemData?.conversionConfig;
  if (!config?.enabled) {
    return null;
  }

  if (config.usePaymentAmount) {
    const fromResponse = response?.paymentDetails?.amount
      ?? response?.amount
      ?? response?.formData?.amount;
    if (fromResponse != null) {
      return Number(fromResponse);
    }

    const fromContent = itemData?.content?.dollarAmount;
    if (fromContent != null) {
      return Number(fromContent);
    }
  }

  if (config.value != null && config.value !== '') {
    return Number(config.value);
  }

  return null;
};

/**
 * Fire conversions if the screen/checkpoint is configured to do so
 * for the given trigger ('load' or 'complete'). Defaults to 'complete'.
 */
export const maybeSendConversionEvents = ({
  shortcode,
  itemData,
  itemType,
  response = null,
  trigger = 'complete'
}) => {
  const config = itemData?.conversionConfig;
  if (!shortcode || !config?.enabled || !itemData?.key) {
    return;
  }

  const configuredTrigger = config.trigger === 'load' ? 'load' : 'complete';
  if (configuredTrigger !== trigger) {
    return;
  }

  const dedupeKey = `${String(shortcode).trim().toUpperCase()}:${itemData.key}:${trigger}`;
  if (firedConversionKeys.has(dedupeKey)) {
    console.log(
      `[Conversion] Skipping duplicate ${itemType} "${itemData.key}" on ${trigger} (already fired this session)`
    );
    return;
  }
  firedConversionKeys.add(dedupeKey);

  const enabledPlatforms = ['meta', 'linkedin', 'google']
    .filter((platform) => config[platform]?.enabled)
    .map((platform) => PLATFORM_LABELS[platform]);

  const platformTargets = {
    metaEventName: config.meta?.eventName || null,
    linkedinConversionRuleUrn: config.linkedin?.conversionRuleUrn || null,
    googleConversionActionId: config.google?.conversionActionId || null
  };

  console.log(
    `[Conversion] Firing ${itemType} "${itemData.key}" on ${trigger} — configured platforms: ${
      enabledPlatforms.length ? enabledPlatforms.join(', ') : 'none'
    }`
  );

  sendConversionEventAsync({
    shortcode,
    itemKey: itemData.key,
    itemType,
    conversionValueOverride: getConversionValueOverride(itemData, response),
    platformTargets
  });
};
